import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FiCheckSquare, 
  FiUsers, 
  FiCalendar, 
  FiEye, 
  FiSearch,
  FiFilter,
  FiRefreshCw,
  FiClock,
  FiMapPin,
  FiUserCheck,
  FiUserX,
  FiEdit3,
  FiBarChart2
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { reuniaoService } from '../../services/api';
import './Presencas.css';

const Presencas = () => {
  const navigate = useNavigate();
  const [reunioes, setReunioes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('todos');
  const [filtroData, setFiltroData] = useState('recentes');

  useEffect(() => {
    carregarReunioes();
  }, []);

  const carregarReunioes = async () => {
    try {
      setLoading(true);
      const response = await reuniaoService.getAll();
      if (response.success) {
        // Buscar estatísticas de presença para cada reunião
        const reunioesComPresenca = await Promise.all(
          response.data.map(async (reuniao) => {
            try {
              const estatisticas = await reuniaoService.getEstatisticas(reuniao.id);
              return {
                ...reuniao,
                total_presencas_registradas: estatisticas.data?.total_presencas_registradas || 0,
                presentes: estatisticas.data?.presentes || 0,
                ausentes: estatisticas.data?.ausentes || 0,
                percentual_presenca: estatisticas.data?.percentual_presenca || 0
              };
            } catch (error) {
              return {
                ...reuniao,
                total_presencas_registradas: 0,
                presentes: 0,
                ausentes: 0,
                percentual_presenca: 0
              };
            }
          })
        );
        setReunioes(reunioesComPresenca || []);
      } else {
        toast.error('Erro ao carregar reuniões');
      }
    } catch (error) {
      console.error('Erro ao carregar reuniões:', error);
      toast.error('Erro ao carregar reuniões');
    } finally {
      setLoading(false);
    }
  };

  const reunioesFiltradas = reunioes.filter(reuniao => {
    const matchSearch = (reuniao.titulo || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                       (reuniao.local && reuniao.local.toLowerCase().includes(searchTerm.toLowerCase()));
    
    let matchStatus = true;
    if (filtroStatus === 'registradas') {
      matchStatus = reuniao.total_presencas_registradas > 0;
    } else if (filtroStatus === 'nao_registradas') {
      matchStatus = reuniao.total_presencas_registradas === 0;
    } else if (filtroStatus === 'concluidas') {
      matchStatus = reuniao.status === 'concluida';
    }
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const reuniaoDate = new Date(reuniao.data);
    reuniaoDate.setHours(0, 0, 0, 0);
    
    let matchData = true;
    if (filtroData === 'hoje') {
      matchData = reuniaoDate.getTime() === today.getTime();
    } else if (filtroData === 'recentes') {
      const umaSemanaAtras = new Date(today);
      umaSemanaAtras.setDate(today.getDate() - 7);
      matchData = reuniaoDate.getTime() >= umaSemanaAtras.getTime();
    } else if (filtroData === 'mes_atual') {
      matchData = reuniaoDate.getMonth() === today.getMonth() && 
                 reuniaoDate.getFullYear() === today.getFullYear();
    }
    
    return matchSearch && matchStatus && matchData;
  });

  const formatarData = (data) => {
    return new Date(data).toLocaleDateString('pt-BR');
  };

  const formatarHora = (hora) => {
    if (hora == null || hora === '') return '—';
    return String(hora).substring(0, 5);
  };

  const getStatusPresenca = (reuniao) => {
    if (reuniao.total_presencas_registradas === 0) {
      return { label: 'Não registrada', classe: 'nao-registrada', icon: FiUserX };
    }
    
    const percentual = reuniao.percentual_presenca || 0;
    if (percentual >= 80) {
      return { label: 'Excelente', classe: 'excelente', icon: FiUserCheck };
    } else if (percentual >= 60) {
      return { label: 'Boa', classe: 'boa', icon: FiUserCheck };
    } else if (percentual >= 40) {
      return { label: 'Regular', classe: 'regular', icon: FiUsers };
    } else {
      return { label: 'Baixa', classe: 'baixa', icon: FiUserX };
    }
  };

  const isReuniaoHoje = (data) => {
    const today = new Date();
    const reuniaoDate = new Date(data);
    return today.toDateString() === reuniaoDate.toDateString();
  };

  const podeRegistrarPresenca = (reuniao) => {
    return reuniao.status === 'concluida' || reuniao.status === 'em_andamento';
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">
          <FiRefreshCw className="spin" />
          <p>Carregando dados de presença...</p>
        </div>
      </div>
    );
  }

  const totalReunioes = reunioes.length;
  const reunioesComPresenca = reunioes.filter(r => r.total_presencas_registradas > 0).length;
  const reunioesSemPresenca = totalReunioes - reunioesComPresenca;
  const mediaPresenca = totalReunioes > 0 ? 
    reunioes.reduce((acc, r) => acc + (r.percentual_presenca || 0), 0) / totalReunioes : 0;

  return (
    <div className="presencas-page">
      <div className="page-header">
        <div className="header-content">
          <h1>
            <FiCheckSquare className="page-icon" />
            Controle de Presenças
          </h1>
          <p>Gerencie a presença dos colaboradores nas reuniões</p>
        </div>
      </div>

      <div className="page-filters">
        <div className="search-box">
          <FiSearch className="search-icon" />
          <input
            type="text"
            placeholder="Buscar por título ou local da reunião..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="filters-row">
          <div className="filter-group">
            <FiFilter className="filter-icon" />
            <select 
              value={filtroStatus} 
              onChange={(e) => setFiltroStatus(e.target.value)}
            >
              <option value="todos">Todas</option>
              <option value="registradas">Com Presença Registrada</option>
              <option value="nao_registradas">Sem Registro</option>
              <option value="concluidas">Reuniões Concluídas</option>
            </select>
          </div>

          <div className="filter-group">
            <select 
              value={filtroData} 
              onChange={(e) => setFiltroData(e.target.value)}
            >
              <option value="todas">Todas as Datas</option>
              <option value="hoje">Hoje</option>
              <option value="recentes">Últimos 7 dias</option>
              <option value="mes_atual">Mês atual</option>
            </select>
          </div>
        </div>

        <button
          className="btn-refresh"
          onClick={carregarReunioes}
          title="Atualizar dados"
        >
          <FiRefreshCw />
        </button>
      </div>

      <div className="presencas-stats">
        <div className="stat-card">
          <span className="stat-number">{totalReunioes}</span>
          <span className="stat-label">Total de Reuniões</span>
        </div>
        <div className="stat-card with-presence">
          <span className="stat-number">{reunioesComPresenca}</span>
          <span className="stat-label">Com Presença</span>
        </div>
        <div className="stat-card without-presence">
          <span className="stat-number">{reunioesSemPresenca}</span>
          <span className="stat-label">Sem Registro</span>
        </div>
        <div className="stat-card average">
          <span className="stat-number">{mediaPresenca.toFixed(1)}%</span>
          <span className="stat-label">Média de Presença</span>
        </div>
        <div className="stat-card">
          <span className="stat-number">{reunioesFiltradas.length}</span>
          <span className="stat-label">Filtradas</span>
        </div>
      </div>

      {reunioesFiltradas.length === 0 ? (
        <div className="empty-state">
          <FiCheckSquare className="empty-icon" />
          <h3>Nenhuma reunião encontrada</h3>
          <p>
            {searchTerm || filtroStatus !== 'todos' || filtroData !== 'recentes'
              ? 'Tente ajustar os filtros de busca'
              : 'Não há reuniões para exibir no período selecionado'
            }
          </p>
        </div>
      ) : (
        <div className="presencas-grid">
          {reunioesFiltradas.map(reuniao => {
            const statusPresenca = getStatusPresenca(reuniao);
            const StatusIcon = statusPresenca.icon;
            const isHoje = isReuniaoHoje(reuniao.data);
            const podeRegistrar = podeRegistrarPresenca(reuniao);
            
            return (
              <div 
                key={reuniao.id} 
                className={`presenca-card ${statusPresenca.classe} ${isHoje ? 'hoje' : ''}`}
              >
                <div className="presenca-header">
                  <div className="reuniao-info-header">
                    <h3>{reuniao.titulo}</h3>
                    <div className="reuniao-meta">
                      <div className="meta-item">
                        <FiCalendar />
                        <span>{formatarData(reuniao.data)}</span>
                        {isHoje && <span className="hoje-badge">Hoje</span>}
                      </div>
                      <div className="meta-item">
                        <FiClock />
                        <span>{formatarHora(reuniao.hora_inicio)}</span>
                      </div>
                      {reuniao.local && (
                        <div className="meta-item">
                          <FiMapPin />
                          <span>{reuniao.local}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="status-presenca">
                    <StatusIcon className="status-icon" />
                    <span className="status-label">{statusPresenca.label}</span>
                  </div>
                </div>
                
                <div className="presenca-body">
                  {reuniao.total_presencas_registradas > 0 ? (
                    <>
                      <div className="presenca-estatisticas">
                        <div className="estatistica-item">
                          <div className="estatistica-valor presentes">
                            {reuniao.presentes}
                          </div>
                          <div className="estatistica-label">Presentes</div>
                        </div>
                        
                        <div className="estatistica-item">
                          <div className="estatistica-valor ausentes">
                            {reuniao.ausentes}
                          </div>
                          <div className="estatistica-label">Ausentes</div>
                        </div>
                        
                        <div className="estatistica-item">
                          <div className="estatistica-valor total">
                            {reuniao.total_presencas_registradas}
                          </div>
                          <div className="estatistica-label">Total</div>
                        </div>
                        
                        <div className="estatistica-item">
                          <div className="estatistica-valor percentual">
                            {(Number(reuniao.percentual_presenca) || 0).toFixed(1)}%
                          </div>
                          <div className="estatistica-label">Presença</div>
                        </div>
                      </div>
                      
                      <div className="barra-presenca">
                        <div 
                          className="barra-progresso"
                          style={{ width: `${Number(reuniao.percentual_presenca) || 0}%` }}
                        />
                      </div>
                    </>
                  ) : (
                    <div className="sem-presenca">
                      <FiUserX className="sem-presenca-icon" />
                      <p>Presença não registrada</p>
                      {podeRegistrar && (
                        <small>Clique em "Registrar Presença" para começar</small>
                      )}
                    </div>
                  )}
                </div>

                <div className="presenca-footer">
                  <div className="presenca-actions">
                    {reuniao.total_presencas_registradas > 0 && (
                      <button
                        className="btn-icon view"
                        onClick={() => navigate(`/presencas/relatorio/${reuniao.id}`)}
                        title="Ver relatório"
                      >
                        <FiBarChart2 />
                      </button>
                    )}
                    
                    {podeRegistrar && (
                      <>
                        <button
                          className="btn-action register"
                          onClick={() => navigate(`/presencas/registro/${reuniao.id}`)}
                        >
                          <FiEdit3 />
                          {reuniao.total_presencas_registradas > 0 ? 'Editar Presença' : 'Registrar Presença'}
                        </button>
                        
                        <button
                          className="btn-icon details"
                          onClick={() => navigate(`/reunioes/${reuniao.id}`)}
                          title="Ver detalhes da reunião"
                        >
                          <FiEye />
                        </button>
                      </>
                    )}
                  </div>
                  
                  <small className="status-reuniao">
                    Status: <strong>{(reuniao.status || '—').replace(/_/g, ' ')}</strong>
                  </small>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Presencas; 